import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload,
  Loader2,
  Database,
  FileType,
  Trash2,
  Tag,
  Car,
  Cpu,
  CheckCircle,
  Plus,
  Search,
  Zap,
  Brain,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOLUTION_TAGS = {
  dpf: "Solución DPF",
  egr: "Solución EGR",
  adblue: "Solución AdBlue",
  dtc: "DTC Removal",
  lambda_off: "Solución Lambda",
  vmax: "Solución VMAX",
  stage1: "Stage 1",
  stage2: "Stage 2",
  eco: "Eco Tuning",
  pop_bang: "Pop & Bang",
  flaps: "Solución Flaps",
  start_stop: "Solución Start/Stop",
  dsg_stage1: "DSG Stage 1",
  dsg_stage2: "DSG Stage 2",
  dsg_tq_unlock: "DSG TQ Unlock",
  hotstart: "Hotstart",
  tva: "TVA",
  swirl_flaps: "Swirl Flaps",
  speed_limiter: "Speed Limiter",
  exhaust_flap: "Exhaust Flap",
  cat: "Solución CAT",
  nox: "Solución NOx",
  o2: "Solución O2",
  torque_monitoring: "Torque Monitoring",
  immo_off: "IMMO Off",
  readiness: "Readiness",
  cold_start: "Cold Start",
};

const AdminSolutionsPage = () => {
  const { token } = useAuth();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const [reanalyzingAll, setReanalyzingAll] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState({});
  const [uploadResult, setUploadResult] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [totalVersions, setTotalVersions] = useState(0);

  const [formData, setFormData] = useState({
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_year: '',
    ecu_type: '',
    engine_code: '',
    horsepower: '',
    notes: '',
    vehicle_type: 'turismo',
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [originalFile, setOriginalFile] = useState(null);
  const [modifiedFile, setModifiedFile] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [similarSolutions, setSimilarSolutions] = useState([]);
  const [fileInfo, setFileInfo] = useState(null);

  const handleOriFileSelect = async (file) => {
    setOriginalFile(file);
    setDuplicateInfo(null);
    setSimilarSolutions([]);
    setFileInfo(null);
    if (!file) return;
    setCheckingDuplicate(true);
    try {
      // Upload file for full duplicate check (hash + metadata analysis)
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/solutions/check-duplicate`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.duplicate) {
        setDuplicateInfo(res.data.solution);
        toast.warning(`ORI DUPLICADO: ${res.data.solution.vehicle} - ${res.data.solution.ecu}`);
      } else if (res.data.similar?.length > 0) {
        setSimilarSolutions(res.data.similar);
        toast.info(`${res.data.similar.length} soluciones similares encontradas`);
      }
      setFileInfo(res.data.file_info || null);
    } catch {}
    finally { setCheckingDuplicate(false); }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchSolutions();
  }, [page, perPage, searchDebounced]);

  const fetchSolutions = async () => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (searchDebounced) params.append('search', searchDebounced);
      const response = await axios.get(`${API}/admin/solutions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSolutions(response.data.solutions || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      setTotalMatches(response.data.total_matches || 0);
      setTotalVersions(response.data.total_versions || 0);
    } catch (error) {
      toast.error('Error al cargar soluciones');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!originalFile || !modifiedFile) {
      toast.error('Debes seleccionar archivo ORI y MOD');
      return;
    }

    if (selectedTags.length === 0) {
      toast.error('Selecciona al menos una solución');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('original_file', originalFile);
      formDataUpload.append('modified_file', modifiedFile);
      formDataUpload.append('vehicle_brand', formData.vehicle_brand);
      formDataUpload.append('vehicle_model', formData.vehicle_model);
      formDataUpload.append('vehicle_year', formData.vehicle_year || '');
      formDataUpload.append('ecu_type', formData.ecu_type);
      formDataUpload.append('engine_code', formData.engine_code || '');
      formDataUpload.append('horsepower', formData.horsepower || '');
      formDataUpload.append('vehicle_type', formData.vehicle_type || 'turismo');
      formDataUpload.append('solutions', selectedTags.join(','));
      formDataUpload.append('notes', formData.notes || '');

      const response = await axios.post(`${API}/admin/solutions/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const analysisInfo = response.data.analysis;
      if (analysisInfo && analysisInfo.hw_id) {
        setUploadResult(analysisInfo);
        toast.success(response.data.is_new ? 'Solución creada con análisis IA' : 'Versión añadida con análisis IA');
      } else {
        toast.success(response.data.is_new ? 'Nueva solución creada' : 'Versión añadida a solución existente');
        setShowUpload(false);
        resetForm();
      }
      fetchSolutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir solución');
    } finally {
      setUploading(false);
    }
  };

  const reanalyzeSolution = async (solutionId) => {
    setReanalyzingId(solutionId);
    try {
      const response = await axios.post(`${API}/admin/solutions/${solutionId}/reanalyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Análisis IA completado');
      setExpandedAnalysis(prev => ({ ...prev, [solutionId]: response.data.analysis }));
      fetchSolutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en análisis IA');
    } finally {
      setReanalyzingId(null);
    }
  };

  const reanalyzeAll = async () => {
    setReanalyzingAll(true);
    try {
      const response = await axios.post(`${API}/admin/solutions/reanalyze-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al re-analizar');
    } finally {
      setReanalyzingAll(false);
    }
  };

  const [relearningId, setRelearningId] = useState(null);

  const relearnPatterns = async (solutionId) => {
    setRelearningId(solutionId);
    try {
      await axios.post(`${API}/admin/solutions/${solutionId}/relearn-patterns`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Patrones re-aprendidos correctamente');
      fetchSolutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al re-aprender patrones');
    } finally {
      setRelearningId(null);
    }
  };

  const toggleAnalysis = (solutionId) => {
    setExpandedAnalysis(prev => ({
      ...prev,
      [solutionId]: prev[solutionId] ? null : true,
    }));
  };

  const deleteSolution = async (solutionId) => {
    if (!confirm('¿Eliminar esta solución?')) return;

    try {
      await axios.delete(`${API}/admin/solutions/${solutionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Solución eliminada');
      fetchSolutions();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const downloadSolutionFile = async (solutionId, fileType) => {
    try {
      const res = await axios.get(`${API}/admin/solutions/${solutionId}/download/${fileType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      const fname = match ? match[1] : `${fileType}_${solutionId}.bin`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Archivo no encontrado' : 'Error al descargar');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_brand: '',
      vehicle_model: '',
      vehicle_year: '',
      ecu_type: '',
      engine_code: '',
      horsepower: '',
      notes: '',
      vehicle_type: 'turismo',
    });
    setSelectedTags([]);
    setOriginalFile(null);
    setModifiedFile(null);
    setUploadResult(null);
    setDuplicateInfo(null);
  };

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // Search is now server-side, no client filter needed

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="admin-solutions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
              BASE DE DATOS DE SOLUCIONES
            </h1>
            <p className="text-zinc-400 mt-1">
              {total} soluciones · Página {page} de {totalPages}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={reanalyzeAll}
              disabled={reanalyzingAll}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="reanalyze-all-btn"
            >
              {reanalyzingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              Re-analizar IA
            </Button>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider"
              data-testid="new-solution-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Solución
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-surface border border-white/10 rounded-md p-5">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-primary" />
              <div>
                <p className="text-zinc-400 text-sm">Total Soluciones</p>
                <p className="font-mono text-2xl font-bold text-white" data-testid="total-solutions-count">{total}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-5">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-zinc-400 text-sm">Total Matches</p>
                <p className="font-mono text-2xl font-bold text-white" data-testid="total-matches-count">
                  {totalMatches}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-5">
            <div className="flex items-center gap-3">
              <FileType className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-zinc-400 text-sm">Total Versiones</p>
                <p className="font-mono text-2xl font-bold text-white" data-testid="total-versions-count">
                  {totalVersions}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por marca, modelo o ECU..."
              className="pl-9 bg-surface border-white/10 text-white"
            />
          </div>
        </div>

        {/* Solutions List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : solutions.length === 0 ? (
          <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
            <Database className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white mb-2">NO HAY SOLUCIONES</h2>
            <p className="text-zinc-400 mb-6">Sube tu primer par ORI/MOD para empezar</p>
            <Button onClick={() => setShowUpload(true)} className="bg-primary hover:bg-primary/90">
              <Plus className="w-5 h-5 mr-2" />
              Subir Solución
            </Button>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {solutions.map((solution) => {
              const analysis = solution.analysis;
              const hasAiAnalysis = analysis && analysis.hw_id && typeof analysis.hw_id === 'object';
              const isExpanded = expandedAnalysis[solution.id];

              return (
              <div
                key={solution.id}
                className="bg-surface border border-white/10 rounded-md p-5 card-hover"
                data-testid={`solution-card-${solution.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Car className="w-6 h-6 text-primary" />
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">
                        {solution.vehicle_brand} {solution.vehicle_model}
                        {solution.vehicle_type === 'industrial' && <span className="ml-2 text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded font-mono">INDUSTRIAL</span>}
                      </h3>
                      <p className="text-zinc-500 text-sm">
                        {solution.vehicle_year && `${solution.vehicle_year} - `}{solution.ecu_type}
                        {solution.engine_code && ` - ${solution.engine_code}`}
                        {solution.horsepower && ` (${solution.horsepower} CV)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadSolutionFile(solution.id, 'ori')}
                      className="text-green-400 hover:text-green-300"
                      title="Descargar ORI"
                      data-testid={`download-ori-btn-${solution.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => reanalyzeSolution(solution.id)}
                      disabled={reanalyzingId === solution.id}
                      className="text-blue-400 hover:text-blue-300"
                      title="Re-analizar con IA"
                      data-testid={`reanalyze-btn-${solution.id}`}
                    >
                      {reanalyzingId === solution.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Brain className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => relearnPatterns(solution.id)}
                      disabled={relearningId === solution.id || !solution.modified_versions?.length}
                      className="text-purple-400 hover:text-purple-300"
                      title="Re-aprender patrones ORI vs MOD"
                      data-testid={`relearn-btn-${solution.id}`}
                    >
                      {relearningId === solution.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteSolution(solution.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* AI Analysis Badge */}
                {hasAiAnalysis && (
                  <div className="mb-3">
                    <button
                      onClick={() => toggleAnalysis(solution.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/15 transition-colors"
                      data-testid={`toggle-analysis-${solution.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Brain className="w-3.5 h-3.5 text-blue-400" />
                        <span className="text-blue-400 text-xs font-medium">ANÁLISIS IA</span>
                        {analysis.model_used && (
                          <span className="text-zinc-600 text-[10px]">({analysis.model_used})</span>
                        )}
                        {analysis.hw_id?.value && (
                          <span className="text-zinc-400 text-xs">HW: {analysis.hw_id.value}</span>
                        )}
                        {analysis.sw_id?.value && (
                          <span className="text-zinc-400 text-xs">SW: {analysis.sw_id.value}</span>
                        )}
                        {analysis.bosch_cal?.cal_variant && (
                          <span className="text-amber-400 text-xs font-bold">CAL: {analysis.bosch_cal.cal_variant}</span>
                        )}
                      </div>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                    </button>
                    {isExpanded && (
                      <div className="mt-2 px-3 py-2 rounded bg-zinc-900/50 border border-white/5 space-y-1.5">
                        {analysis.hw_id?.value && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500 w-24">HW ID</span>
                            <span className="text-white font-mono">{analysis.hw_id.value}</span>
                            <span className="text-zinc-600">({Math.round((analysis.hw_id.confidence || 0) * 100)}%)</span>
                          </div>
                        )}
                        {analysis.sw_id?.value && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500 w-24">SW ID</span>
                            <span className="text-white font-mono">{analysis.sw_id.value}</span>
                            <span className="text-zinc-600">({Math.round((analysis.sw_id.confidence || 0) * 100)}%)</span>
                          </div>
                        )}
                        {analysis.ecu_family_hint?.value && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500 w-24">ECU</span>
                            <span className="text-white">{analysis.ecu_family_hint.value}</span>
                            <span className="text-zinc-600">({Math.round((analysis.ecu_family_hint.confidence || 0) * 100)}%)</span>
                          </div>
                        )}
                        {analysis.oem_hint?.value && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500 w-24">OEM</span>
                            <span className="text-white">{analysis.oem_hint.value}</span>
                            <span className="text-zinc-600">({Math.round((analysis.oem_hint.confidence || 0) * 100)}%)</span>
                          </div>
                        )}
                        {analysis.other_ids?.length > 0 && (
                          <>
                            <div className="border-t border-white/5 pt-1.5 mt-1.5" />
                            {analysis.other_ids.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <span className="text-zinc-500 w-24 truncate" title={item.label}>{item.label}</span>
                                <span className="text-cyan-400 font-mono">{item.value}</span>
                                {item.confidence > 0 && <span className="text-zinc-600">({Math.round(item.confidence * 100)}%)</span>}
                              </div>
                            ))}
                          </>
                        )}
                        {(analysis.summary_es || analysis.summary) && (
                          <p className="text-zinc-400 text-xs pt-1.5 border-t border-white/5 leading-relaxed">{analysis.summary_es || analysis.summary}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!hasAiAnalysis && analysis && (
                  <div className="mb-3 flex items-center gap-2 px-3 py-1.5 rounded bg-yellow-500/10 border border-yellow-500/20">
                    <Cpu className="w-3.5 h-3.5 text-yellow-500" />
                    <span className="text-yellow-500 text-xs">Análisis básico - Pulsa</span>
                    <Brain className="w-3 h-3 text-yellow-500" />
                    <span className="text-yellow-500 text-xs">para IA completa</span>
                  </div>
                )}

                {/* Pattern Analysis (ORI vs MOD) */}
                {solution.pattern_analysis && (solution.pattern_analysis.detected_modifications || solution.pattern_analysis.summary_es || solution.pattern_analysis.pct_changed !== undefined) && (
                  <div className="mb-3 px-3 py-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Zap className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-purple-400 text-xs font-medium">PATRONES APRENDIDOS</span>
                      {solution.pattern_analysis.model_used && (
                        <span className="text-zinc-600 text-[10px]">via {solution.pattern_analysis.model_used}</span>
                      )}
                    </div>
                    {solution.pattern_analysis.detected_modifications && (
                      <div className="flex flex-wrap gap-1">
                        {solution.pattern_analysis.detected_modifications.map((mod, i) => (
                          <span key={i} className="text-[10px] bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded">
                            {mod.type} ({Math.round((mod.confidence || 0) * 100)}%)
                          </span>
                        ))}
                      </div>
                    )}
                    {solution.pattern_analysis.pct_changed !== undefined && !solution.pattern_analysis.detected_modifications && (
                      <p className="text-zinc-400 text-[10px]">Diff: {solution.pattern_analysis.total_changed || 0} bytes ({solution.pattern_analysis.pct_changed}%) en {solution.pattern_analysis.total_regions || 0} regiones</p>
                    )}
                    {solution.pattern_analysis.error && (
                      <p className="text-red-400 text-[10px]">{solution.pattern_analysis.error}</p>
                    )}
                    {solution.pattern_analysis.summary_es && (
                      <p className="text-zinc-400 text-[10px] mt-1">{solution.pattern_analysis.summary_es}</p>
                    )}
                  </div>
                )}

                {/* Versions */}
                <div className="space-y-2 mb-4">
                  {solution.modified_versions?.map((version, idx) => (
                    <div
                      key={version.id}
                      className="bg-surface-highlight rounded-md p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-sm font-medium">
                          Versión {idx + 1}
                        </span>
                        <span className="text-zinc-500 text-xs">
                          {new Date(version.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {version.solution_names?.map((name, i) => (
                          <span
                            key={i}
                            className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                      {version.notes && (
                        <p className="text-zinc-400 text-xs mt-2">{version.notes}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <span className="text-zinc-500 text-sm">
                    <Zap className="w-4 h-4 inline mr-1 text-yellow-500" />
                    {solution.match_count || 0} matches
                  </span>
                  <span className="font-mono text-xs text-zinc-600">
                    {solution.original_hash?.substring(0, 12)}...
                  </span>
                </div>
              </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 flex-wrap gap-3" data-testid="pagination">
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">Mostrar</span>
                <select
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="bg-surface-highlight border border-white/10 text-white text-sm rounded px-2 py-1"
                  data-testid="per-page-select"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-zinc-500 text-sm">de {total}</span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="border-white/10 text-white px-2"
                >
                  1
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="border-white/10 text-white"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-white text-sm px-3 py-1 bg-primary/20 rounded font-mono">{page}</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="border-white/10 text-white"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="border-white/10 text-white px-2"
                >
                  {totalPages}
                </Button>
              </div>
            </div>
          )}
        </>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">SUBIR SOLUCIÓN ORI/MOD</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-6">
            {/* AI Analysis Result after upload */}
            {uploadResult && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-blue-400" />
                  <h4 className="text-blue-400 font-heading text-sm font-bold">ANÁLISIS IA DEL FIRMWARE</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {uploadResult.hw_id?.value && (
                    <div><span className="text-zinc-500">HW: </span><span className="text-white font-mono">{uploadResult.hw_id.value}</span> <span className="text-zinc-600">({Math.round((uploadResult.hw_id.confidence || 0) * 100)}%)</span></div>
                  )}
                  {uploadResult.sw_id?.value && (
                    <div><span className="text-zinc-500">SW: </span><span className="text-white font-mono">{uploadResult.sw_id.value}</span> <span className="text-zinc-600">({Math.round((uploadResult.sw_id.confidence || 0) * 100)}%)</span></div>
                  )}
                  {uploadResult.ecu_family_hint?.value && (
                    <div><span className="text-zinc-500">ECU: </span><span className="text-white">{uploadResult.ecu_family_hint.value}</span></div>
                  )}
                  {uploadResult.oem_hint?.value && (
                    <div><span className="text-zinc-500">OEM: </span><span className="text-white">{uploadResult.oem_hint.value}</span></div>
                  )}
                </div>
                {uploadResult.summary && (
                  <p className="text-zinc-400 text-xs mt-2 pt-2 border-t border-blue-500/20">{uploadResult.summary}</p>
                )}
                <Button type="button" size="sm" variant="outline" className="mt-3 border-blue-500/30 text-blue-400" onClick={() => { setShowUpload(false); resetForm(); }}>
                  Cerrar
                </Button>
              </div>
            )}

            {!uploadResult && (<>
            {/* Vehicle Type Toggle */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Tipo de Vehículo</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setFormData({...formData, vehicle_type: 'turismo'})} data-testid="upload-vehicle-type-turismo"
                  className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${formData.vehicle_type === 'turismo' ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                  Turismo
                </button>
                <button type="button" onClick={() => setFormData({...formData, vehicle_type: 'industrial'})} data-testid="upload-vehicle-type-industrial"
                  className={`flex-1 py-3 rounded-md border text-sm font-medium transition-all ${formData.vehicle_type === 'industrial' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'}`}>
                  Industrial
                </button>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Marca</Label>
                <Input
                  value={formData.vehicle_brand}
                  onChange={(e) => setFormData({...formData, vehicle_brand: e.target.value})}
                  placeholder="BMW, Audi, VW..."
                  required
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Modelo</Label>
                <Input
                  value={formData.vehicle_model}
                  onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})}
                  placeholder="320d, A4, Golf..."
                  required
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Año <span className="text-zinc-600 text-xs">(opcional)</span></Label>
                <Input
                  value={formData.vehicle_year}
                  onChange={(e) => setFormData({...formData, vehicle_year: e.target.value})}
                  placeholder="2020"
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Tipo ECU</Label>
                <Input
                  value={formData.ecu_type}
                  onChange={(e) => setFormData({...formData, ecu_type: e.target.value})}
                  placeholder="Bosch EDC17..."
                  required
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Motor (opcional)</Label>
                <Input
                  value={formData.engine_code}
                  onChange={(e) => setFormData({...formData, engine_code: e.target.value})}
                  placeholder="N47D20, CFFB..."
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">CV (opcional)</Label>
                <Input
                  value={formData.horsepower}
                  onChange={(e) => setFormData({...formData, horsepower: e.target.value})}
                  placeholder="184"
                  className="bg-surface-highlight border-white/10 text-white"
                />
              </div>
            </div>

            {/* Files */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Archivo ORIGINAL (.bin, .hex)</Label>
                <div className={`dropzone rounded-md p-4 text-center ${duplicateInfo ? 'border-yellow-500' : originalFile ? 'border-green-500' : ''}`}>
                  {checkingDuplicate ? (
                    <div className="flex items-center gap-2 justify-center">
                      <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                      <span className="text-blue-400 text-sm">Verificando duplicados...</span>
                    </div>
                  ) : originalFile ? (
                    <div className="flex items-center gap-2 justify-center">
                      {duplicateInfo ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      <span className={`text-sm truncate max-w-[150px] ${duplicateInfo ? 'text-yellow-400' : 'text-green-400'}`}>
                        {originalFile.name}
                      </span>
                    </div>
                  ) : (
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto" />
                  )}
                  {duplicateInfo && (
                    <div className="mt-2 bg-yellow-500/10 border border-yellow-500/20 rounded px-2 py-1.5">
                      <p className="text-yellow-400 text-xs font-medium">ORI DUPLICADO EXACTO</p>
                      <p className="text-zinc-400 text-[10px]">{duplicateInfo.vehicle} - {duplicateInfo.ecu} ({duplicateInfo.versions} versiones)</p>
                      {duplicateInfo.solutions?.length > 0 && (
                        <p className="text-zinc-500 text-[10px]">Soluciones: {duplicateInfo.solutions.join(', ')}</p>
                      )}
                      <p className="text-zinc-500 text-[10px]">La versión MOD se añadirá a la solución existente</p>
                    </div>
                  )}
                  {!duplicateInfo && similarSolutions.length > 0 && (
                    <div className="mt-2 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-1.5 text-left">
                      <p className="text-blue-400 text-xs font-medium mb-1">Soluciones similares encontradas ({similarSolutions.length})</p>
                      {similarSolutions.slice(0, 5).map((s, i) => (
                        <div key={s.id} className="flex items-center justify-between py-0.5 border-b border-white/5 last:border-0">
                          <div>
                            <span className="text-zinc-300 text-[10px]">{s.vehicle} {s.ecu}</span>
                            {s.solutions?.length > 0 && (
                              <span className="text-zinc-500 text-[10px] ml-1">({s.solutions.join(', ')})</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {s.match_reasons.map((r, j) => (
                              <span key={j} className="text-[9px] bg-blue-500/20 text-blue-400 px-1 rounded">{r}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {fileInfo && (
                    <div className="mt-1 flex items-center gap-2 justify-center flex-wrap">
                      {fileInfo.ecu_family && <span className="text-[9px] bg-primary/20 text-primary px-1 rounded">{fileInfo.ecu_family}</span>}
                      {fileInfo.sw_id && <span className="text-[9px] bg-zinc-500/20 text-zinc-400 px-1 rounded">SW:{fileInfo.sw_id}</span>}
                      {fileInfo.bosch_cal && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1 rounded">CAL:{fileInfo.bosch_cal}</span>}
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => e.target.files[0] && handleOriFileSelect(e.target.files[0])}
                    className="hidden"
                    id="ori-file"
                    accept=".bin,.hex,.ori"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => document.getElementById('ori-file').click()}
                    className="mt-2"
                  >
                    {originalFile ? 'Cambiar' : 'Seleccionar ORI'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Archivo MODIFICADO</Label>
                <div className={`dropzone rounded-md p-4 text-center ${modifiedFile ? 'border-primary' : ''}`}>
                  {modifiedFile ? (
                    <div className="flex items-center gap-2 justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      <span className="text-primary text-sm truncate max-w-[150px]">
                        {modifiedFile.name}
                      </span>
                    </div>
                  ) : (
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto" />
                  )}
                  <input
                    type="file"
                    onChange={(e) => setModifiedFile(e.target.files[0])}
                    className="hidden"
                    id="mod-file"
                    accept=".bin,.hex,.mod"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => document.getElementById('mod-file').click()}
                    className="mt-2"
                  >
                    {modifiedFile ? 'Cambiar' : 'Seleccionar MOD'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Solution Tags */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Soluciones aplicadas</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SOLUTION_TAGS).map(([key, name]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleTag(key)}
                    className={`px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedTags.includes(key)
                        ? 'bg-primary text-white'
                        : 'bg-surface-highlight text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Tag className="w-3 h-3 inline mr-1" />
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Notas (opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Información adicional..."
                className="bg-surface-highlight border-white/10 text-white"
              />
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-primary hover:bg-primary/90 font-heading uppercase tracking-wider"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analizando con IA y subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Subir Solución
                </>
              )}
            </Button>
            </>)}
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminSolutionsPage;
